import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { SCRAPE_CATEGORY_KEY } from "./keys";

import { ScrapedCategoryRepository } from "@/repositories/scrape-category";
import { PaginatedResponse } from "@/types";
import {
  ScrapedCategory,
  UpdateScrapedCategoryBrand,
} from "@/types/scrape-category";

export const useUpdateScrapedCategoryBrand = () => {
  const queryClient = useQueryClient();
  const scrapedCategoryRepository = useRef(new ScrapedCategoryRepository());

  return useMutation({
    mutationFn: async (data: UpdateScrapedCategoryBrand) => {
      return await scrapedCategoryRepository.current.updateBrand(data);
    },
    onMutate: async (newData: UpdateScrapedCategoryBrand) => {
      const previousQueries = queryClient.getQueriesData({
        predicate: (query) =>
          query.queryKey[0] === SCRAPE_CATEGORY_KEY &&
          query.queryKey[1] === "LIST",
      });

      queryClient.setQueriesData(
        {
          predicate: (query) =>
            query.queryKey[0] === SCRAPE_CATEGORY_KEY &&
            query.queryKey[1] === "LIST",
        },
        (oldData) => {
          if (!oldData) return oldData;
          const cachedData = oldData as PaginatedResponse<ScrapedCategory>;

          return {
            ...cachedData,
            data: cachedData.data.map((category) => {
              if (category.id === newData.categoryId) {
                return {
                  ...category,
                  brandRelations: category.brandRelations.map(
                    (brandCategory) =>
                      brandCategory.brand.id === newData.brandId
                        ? {
                            ...brandCategory,
                            blacklist: newData.blacklist,
                          }
                        : brandCategory,
                  ),
                };
              }

              return category;
            }),
          };
        },
      );

      return { previousQueries };
    },
    onError: (_err, _newData, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });
};
